/** @jsxImportSource @emotion/react */
import req from '@sportninja/common/api/request'
import { mapFormAnswersObject } from 'src/utils/mapFormAnswers'
import { mapReservedAnswersToRoster } from 'src/utils/mapReservedAnswersToRoster'
import { CustomForm } from 'src/types/CustomForm'
import { AnswerState } from 'src/utils/dynamicFormReducer'

export interface SubmitDynamicRegistrationParams {
  scheduleId: string
  registrationUid: string
  isTeamRegistration: boolean
  isApprovedPlayer: boolean
  answers: AnswerState
  formDefinition: CustomForm
  playerContext?: {
    id: string
  }
  teamContext?: {
    uid: string
    rosterId: string
  }
}

/**
 * 🔄 submitDynamicRegistration: Submits dynamic form data for registration updates
 *
 * This function handles the submission of dynamic form data to the registration update API.
 * It transforms answers into the key-value payload format and makes the necessary API calls
 * for both registration updates and roster updates (for approved players).
 *
 * @param params - The submission parameters including form data and context
 * @throws API errors for handling by the caller
 */
export const submitDynamicRegistration = async ({
  scheduleId,
  registrationUid,
  isTeamRegistration,
  isApprovedPlayer,
  answers,
  formDefinition,
  playerContext,
  teamContext,
}: SubmitDynamicRegistrationParams): Promise<void> => {
  console.log(
    '🔄 submitDynamicRegistration: Starting dynamic form submission',
    {
      scheduleId,
      registrationUid,
      isTeamRegistration,
      isApprovedPlayer,
      answersCount: Object.keys(answers).length,
    }
  )

  // Transform answers into the key-value payload format used by New_CustomRegister
  const dynamicPayload = mapFormAnswersObject(formDefinition, answers)

  // Build the final payload with the form data and registration type
  const payload = {
    form: dynamicPayload.form, // Use the mapped form data under 'form' property
    registration_type_id: isTeamRegistration ? 1 : 2,
  }

  console.log('🔄 submitDynamicRegistration: Payload prepared', {
    formSections: Object.keys(payload.form).length,
    registrationTypeId: payload.registration_type_id,
    payload,
  })

  // Submit the main registration update
  await req(
    `/registration/${scheduleId}/admin/team-registration/${registrationUid}`,
    {
      method: 'PUT',
      body: JSON.stringify(payload),
    }
  )

  console.log('🔄 submitDynamicRegistration: Registration update completed')

  // If this is an approved player, also update the roster
  if (
    isApprovedPlayer &&
    playerContext?.id &&
    teamContext?.uid &&
    teamContext?.rosterId
  ) {
    console.log(
      '🔄 submitDynamicRegistration: Updating roster for approved player',
      {
        playerId: playerContext.id,
        teamUid: teamContext.uid,
        rosterId: teamContext.rosterId,
      }
    )

    console.log(
      '🔄 submitDynamicRegistration: Answers and form definition',
      answers,
      formDefinition
    )

    // Map ID-based answers to roster payload fields using reserved field mappings
    const baseRoster = mapReservedAnswersToRoster(formDefinition, answers)

    // Build roster update payload with mapped form data
    const rosterPayload: Record<string, string> = {}
    if (baseRoster.name_first) {
      rosterPayload.name_first = String(baseRoster.name_first)
    }
    if (baseRoster.name_last) {
      rosterPayload.name_last = String(baseRoster.name_last)
    }
    if (baseRoster.email) {
      rosterPayload.email = String(baseRoster.email)
    }
    if (baseRoster.birth_date) {
      rosterPayload.birth_date = String(baseRoster.birth_date)
    }
    if (baseRoster.player_type_id) {
      rosterPayload.player_type_id = String(baseRoster.player_type_id)
    }
    if (baseRoster.preferred_jersey_number) {
      rosterPayload.player_number = String(baseRoster.preferred_jersey_number)
    }
    if (baseRoster.other_membership_number) {
      rosterPayload.customer_identifier = String(
        baseRoster.other_membership_number
      )
    }

    await req(
      `/teams/${teamContext.uid}/rosters/${teamContext.rosterId}/players/${playerContext.id}`,
      {
        method: 'PUT',
        body: JSON.stringify(rosterPayload),
      }
    )

    console.log('🔄 submitDynamicRegistration: Roster update completed')
  }

  console.log(
    '🔄 submitDynamicRegistration: All updates completed successfully'
  )
}
